import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { NumField } from "@/components/quote/QuoteParts";
import { PatchProof, PatchTip } from "@/components/quote/PatchProof";

const n = (v) => parseFloat(v || "0") || 0;
export const emptyPatch = () => ({ w: "", h: "", min: "", sec: "", source: "timed", label: "", proof: null });
export const patchSecs = (p) => n(p?.min) * 60 + n(p?.sec);
export const patchRate = (p) => (p && n(p.w) > 0 && n(p.h) > 0 && patchSecs(p) > 0 ? patchSecs(p) / (n(p.w) * n(p.h)) : 0);
export const patchBody = (p) => ({ w_in: n(p.w), h_in: n(p.h), seconds: patchSecs(p), source: p.source || "timed", label: p.label || null });
const fromSecs = (w, h, s, source, label, proof = null) => ({ w: String(w), h: String(h), min: String(Math.floor(s / 60)), sec: String(Math.round((s % 60) * 10) / 10), source, label, proof });
const clock = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
const btn = (on) => `rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${on ? "border-[#4CCC06] text-[#4CCC06]" : "border-[#555] text-white hover:border-[#4CCC06]"} disabled:opacity-40`;

const SavedList = ({ list, itemType, onUse, onDelete, testId }) => {
  if (!list.length) return <p className="text-[11px] text-[#A0A0A0]" data-testid={`${testId}-saved-empty`}>No saved patches for this builder yet. Time one and tap "Save this patch".</p>;
  const sorted = [...list].sort((a, b) => (b.item_type === itemType) - (a.item_type === itemType));
  return sorted.map((s) => (
    <div key={s.id} className="mb-1.5 flex items-center gap-2 rounded-[10px] border border-[#333] bg-[#0A0A0A] p-2" data-testid={`${testId}-saved-${s.id}`}>
      <div className="min-w-0 flex-1"><p className="truncate text-[12px] font-bold">{s.name}{s.proof_id && <span className="ml-1.5 text-[10px] font-semibold text-[#4CCC06]" data-testid={`${testId}-saved-proof-${s.id}`}>· {s.proof_kind === "video" ? "video" : "photo"} proof</span>}</p>
        <p className="text-[10px] text-[#A0A0A0]">{s.w_in}" x {s.h_in}" in {clock(s.seconds)} · {s.sec_per_sq_in.toFixed(2)} sec/sq in</p></div>
      <button onClick={() => onUse(s)} className="rounded-full bg-[#4CCC06] px-3 py-1 text-[10px] font-extrabold text-black" data-testid={`${testId}-saved-use-${s.id}`}>USE</button>
      <button onClick={() => onDelete(s)} className="px-1 text-[10px] font-bold text-[#EF4444]" data-testid={`${testId}-saved-del-${s.id}`}>Delete</button>
    </div>
  ));
};

const PortalAvg = ({ rec, onUse, testId }) => {
  if (!rec) return <p className="text-[11px] text-[#A0A0A0]">Loading…</p>;
  if (!rec.enough) return <p className="text-[11px] leading-4 text-[#A0A0A0]" data-testid={`${testId}-portal-none`}>Not enough portal data yet. An average needs real timings from at least {rec.needed} locations ({rec.locations} so far). Time a test patch for now.</p>;
  return (
    <div className="flex items-center gap-3" data-testid={`${testId}-portal-result`}>
      <p className="flex-1 text-[11px] leading-4">Average of <b>{rec.locations} locations</b>{rec.same_species ? "" : " (same material, any species)"}: <b className="text-[#4CCC06]">{rec.sec_per_sq_in} sec/sq in</b>. Most are between {rec.low} and {rec.high}. Names are never shared.</p>
      <button onClick={onUse} className="rounded-full bg-[#4CCC06] px-3 py-1.5 text-[10px] font-extrabold text-black" data-testid={`${testId}-portal-use`}>USE IT</button>
    </div>
  );
};

export const PatchEditor = ({ value, onChange, builder, itemType, material, species, testId, showTip }) => {
  const p = value || emptyPatch();
  const [panel, setPanel] = useState(null);
  const [saved, setSaved] = useState([]);
  const [rec, setRec] = useState(null);
  const set = (ch) => onChange({ ...p, source: "timed", label: "", ...ch });
  const rate = patchRate(p);
  const toggle = async (k) => {
    if (panel === k) return setPanel(null);
    setPanel(k);
    try {
      if (k === "saved") setSaved(await api(`/quote-builder/patches?builder=${builder}`));
      else { setRec(null); setRec(await api(`/quote-builder/patches/recommended?builder=${builder}&item_type=${itemType || "_main"}&material=${material}&species=${encodeURIComponent(species || "")}`)); }
    } catch (e) { toast.error(e.message); setPanel(null); }
  };
  const save = async () => {
    const name = window.prompt("Name this test patch, e.g. \"Oak cabinet doors, heavy stain\"");
    if (!name?.trim()) return;
    try {
      await api("/quote-builder/patches", { method: "POST", body: { name: name.trim(), builder, item_type: itemType || null, material, species, ...patchBody(p), proof_id: p.proof?.id || null, proof_kind: p.proof?.kind || null } });
      onChange({ ...p, label: name.trim() }); toast.success("Test patch saved for future jobs");
    } catch (e) { toast.error(e.message); }
  };
  const del = async (s) => { try { await api(`/quote-builder/patches/${s.id}`, { method: "DELETE" }); setSaved((x) => x.filter((y) => y.id !== s.id)); } catch (e) { toast.error(e.message); } };
  return (
    <div data-testid={testId}>
      {showTip && <PatchTip testId={`${testId}-tip`} />}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <NumField label="Width (in)" value={p.w} onChange={(v) => set({ w: v })} testId={`${testId}-w`} />
        <NumField label="Height (in)" value={p.h} onChange={(v) => set({ h: v })} testId={`${testId}-h`} />
        <NumField label="Minutes" value={p.min} onChange={(v) => set({ min: v })} testId={`${testId}-min`} />
        <NumField label="Seconds" value={p.sec} onChange={(v) => set({ sec: v })} testId={`${testId}-sec`} />
      </div>
      <p className={`mt-1.5 text-[11px] ${rate ? "text-[#E8E8E8]" : "text-[#F59E0B]"}`} data-testid={`${testId}-rate`}>
        {rate ? <>Cleans at <b className="text-[#4CCC06]">{rate.toFixed(2)} sec per sq in</b>{p.source === "portal" ? " · portal average" : p.label ? ` · ${p.label}` : ""}</> : "Enter the patch size and how long it took to clean."}
      </p>
      {p.source !== "portal" && <PatchProof proof={p.proof} onChange={(proof) => onChange({ ...p, proof })} testId={`${testId}-proof`} />}
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={() => toggle("saved")} className={btn(panel === "saved")} data-testid={`${testId}-saved-btn`}>My saved patches</button>
        <button onClick={() => toggle("portal")} className={btn(panel === "portal")} data-testid={`${testId}-portal-btn`}>Portal average</button>
        <button onClick={save} disabled={!rate || p.source === "portal"} className={btn(false)} data-testid={`${testId}-save-btn`}>Save this patch</button>
      </div>
      {panel && (
        <div className="mt-2 rounded-[10px] border border-[#333] bg-[#121212] p-2.5">
          {panel === "saved" ? <SavedList list={saved} itemType={itemType} testId={testId} onDelete={del} onUse={(s) => { onChange(fromSecs(s.w_in, s.h_in, s.seconds, "saved", s.name, s.proof_id ? { id: s.proof_id, kind: s.proof_kind || "photo" } : null)); setPanel(null); }} />
            : <PortalAvg rec={rec} testId={testId} onUse={() => { onChange(fromSecs(4, 4, rec.sec_per_sq_in * 16, "portal", "Portal average")); setPanel(null); }} />}
        </div>
      )}
    </div>
  );
};
